import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { showToastMessage } from "common/service/CommonService";
import { queryClient } from "api/queryClient";
import { useQueryWorkflowData } from "modules/Library/Workflow/api/useWorkflowQuery";
import { validations } from "validations/FormValidations";

interface AddQueryModalProps {
  isOpen: boolean;
  toggle: () => void;
  isRespond?: boolean;
  studyid?: number;
  stageid?: number;
  querymasterid?: number;
}

const AddQueryModal: React.FC<AddQueryModalProps> = ({
  isOpen,
  toggle,
  isRespond = false,
  studyid,
  stageid,
  querymasterid,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      remarks: "",
    },
    mode:"onBlur",
  });

  const { raiseQueryMutation, updateQueryMutation } = useQueryWorkflowData({
    studyid,
    pagenumber: 1,
    pagesize: 10,
  });

  const fields: FormFieldConfig[] = [
    {
      name: "remarks",
      label: isRespond ? "Notes" : "Raise Query / Remarks",
      type: "textarea",
      placeholder: isRespond
        ? "Enter notes here..."
        : "Enter query or remarks here...",
      required: true,
      rules: {
         maxLength: {
            value: 300,
            message: "Maximum 300 characters allowed",
          },
      },
    },
  ];

  const onSubmit = (data: any) => {
    if (isRespond) {
      const payload = {
        studyid,
        querymasterid,
        currentstageid: stageid,
        querymessage: data.remarks || "",
        querystatus: "RESPONDED",
        updatereason: "Query Respond ",
      };

      updateQueryMutation.mutate(payload, {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries({
            queryKey: ["workflow-query-selectall"],
          });
          showToastMessage(
            "success",
            response?.message || "Response submitted successfully",
          );
          reset();
          toggle();
        },
        onError: (error: any) => {
          showToastMessage(
            "error",
            error?.message || "Failed to submit response",
          );
        },
      });
    } else {
      const payload = {
        studyid: studyid,
        currentstageid: stageid,
        querymessage: data.remarks,
      };

      raiseQueryMutation.mutate(payload, {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries({
            queryKey: ["workflow-query-selectall"],
          });
          showToastMessage(
            "success",
            response?.message || "Query raised successfully",
          );
          reset();
          toggle();
        },
        onError: (error: any) => {
          showToastMessage("error", error?.message || "Failed to raise query");
        },
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => {
        toggle();
        reset();
      }}
      size="lg"
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader
        toggle={() => {
          toggle();
          reset();
        }}
        className="p-3"
      >
        <h5 className="fw-bold text-dark mb-0">
          {isRespond ? "Respond Query" : "Add New Query"}
        </h5>
      </ModalHeader>
      <ModalBody>
        <CommonForm
          fields={fields}
          control={control}
          errors={errors}
          columns={1}
        />
      </ModalBody>
      <ModalFooter className="p-3 border-top">
        <Button
          color="primary"
          outline
          onClick={() => {
            toggle();
            reset();
          }}
          className="fw-medium"
        >
          Cancel
        </Button>
        <Button
          color="success"
          outline
          className="fw-bold px-4"
          onClick={handleSubmit(onSubmit)}
          disabled={
            raiseQueryMutation.isPending || updateQueryMutation.isPending
          }
        >
          {(raiseQueryMutation.isPending || updateQueryMutation.isPending) && (
            <Spinner size="sm" className="me-2" />
          )}
          {isRespond ? "Respond" : "Raise"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddQueryModal;
